/**
 * Sheet (drawer) variant styles (class-variance-authority)
 * @layer atom
 * @package @plerity/ui-core
 *
 * The ModalOverlay is a flex container pinned to the viewport; the Modal panel
 * is aligned to the chosen edge. Slide motion reuses the tokens-apps
 * `animate-slide-in-left|right` / `animate-fade-in-up|down` keyframes with
 * `--motion-slide-distance` overridden to 100% (full-panel travel), and plays
 * them REVERSED on exit (RAC keeps the node mounted while `data-exiting` runs).
 * `motion-reduce:` removes every animation (no slide).
 */

import { cva } from "class-variance-authority";

export type SheetSide = "right" | "left" | "bottom" | "top";
export type SheetSize = "sm" | "md" | "lg" | "full";

const REDUCED = "motion-reduce:data-[entering]:animate-none motion-reduce:data-[exiting]:animate-none";
const REVERSE_ON_EXIT = "data-[exiting]:[animation-direction:reverse]";

/** Scrim. z-[9999] aligns with Modal (--z-modal). `bg-black/50` is dark-neutral. */
export const sheetOverlay = cva(
  [
    "fixed inset-0 z-[9999] flex bg-black/50",
    "data-[entering]:animate-fade-in data-[exiting]:animate-fade-in",
    REVERSE_ON_EXIT,
    REDUCED,
  ].join(" "),
  {
    variants: {
      side: {
        right: "justify-end",
        left: "justify-start",
        bottom: "items-end",
        top: "items-start",
      },
    },
    defaultVariants: { side: "right" },
  }
);

export const sheetPanel = cva(
  [
    "flex flex-col overflow-hidden bg-white text-gray-900 shadow-xl outline-none",
    "dark:bg-gray-800 dark:text-gray-100",
    "[--motion-slide-distance:100%]",
    REVERSE_ON_EXIT,
    REDUCED,
  ].join(" "),
  {
    variants: {
      side: {
        right:
          "h-full w-full border-l border-gray-200 dark:border-gray-700 data-[entering]:animate-slide-in-right data-[exiting]:animate-slide-in-right",
        left: "h-full w-full border-r border-gray-200 dark:border-gray-700 data-[entering]:animate-slide-in-left data-[exiting]:animate-slide-in-left",
        bottom:
          "w-full rounded-t-xl border-t border-gray-200 dark:border-gray-700 data-[entering]:animate-fade-in-up data-[exiting]:animate-fade-in-up",
        top: "w-full rounded-b-xl border-b border-gray-200 dark:border-gray-700 data-[entering]:animate-fade-in-down data-[exiting]:animate-fade-in-down",
      },
      size: {
        sm: "",
        md: "",
        lg: "",
        full: "",
      },
    },
    compoundVariants: [
      // Left/right: size = max width
      { side: ["left", "right"], size: "sm", class: "max-w-xs" },
      { side: ["left", "right"], size: "md", class: "max-w-md" },
      { side: ["left", "right"], size: "lg", class: "max-w-2xl" },
      { side: ["left", "right"], size: "full", class: "max-w-full" },
      // Top/bottom: size = max height
      { side: ["top", "bottom"], size: "sm", class: "max-h-[40vh]" },
      { side: ["top", "bottom"], size: "md", class: "max-h-[60vh]" },
      { side: ["top", "bottom"], size: "lg", class: "max-h-[85vh]" },
      { side: ["top", "bottom"], size: "full", class: "h-full max-h-full rounded-none" },
    ],
    defaultVariants: { side: "right", size: "md" },
  }
);

export const sheetHeader =
  "flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-700";
export const sheetTitle = "text-lg font-semibold text-gray-900 dark:text-gray-100";
export const sheetDescription = "mt-1 text-sm text-gray-600 dark:text-gray-400";
export const sheetBody = "min-h-0 flex-1 overflow-y-auto px-6 py-4";
export const sheetFooter =
  "flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700";
export const sheetCloseButton =
  "-mr-2 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200";
